import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import { People as PeopleIcon, Lock as LockIcon } from '@mui/icons-material';
import { useModal } from '../../contexts/ModalContext';
import { useLoader } from '../../contexts/LoaderContext';
import api from '../../services/api';

const TAX_RATE = 0.18; // 18% GST

const AddUserSlotModal = ({ open, onClose, currentPlan }) => {
  const [slots, setSlots] = useState('0');
  const [cost, setCost] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openSuccessModal, openErrorModal } = useModal();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    const perUser = currentPlan?.pricing?.per_user_cost ?? currentPlan?.per_user_cost;
    if (perUser != null) {
      // Treat empty or invalid input as 0
      const slotCount = parseInt(slots, 10);
      const validSlotCount = isNaN(slotCount) ? 0 : slotCount;
      const baseCost = validSlotCount * perUser;
      const taxAmount = baseCost * TAX_RATE;
      setCost(baseCost);
      setTax(taxAmount);
      setTotalCost(baseCost + taxAmount);
    }
  }, [slots, currentPlan]);

  const handleSlotsChange = (e) => {
    let value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    if (value.length > 1) {
      value = value.replace(/^0+/, '');
      if (value === '') value = '0';
    }
    setSlots(value);
  };

  const handleSlotsFocus = (e) => {
    if (slots === '0') setSlots('');
  };

  const handleSlotsBlur = (e) => {
    if (slots === '') setSlots('0');
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    onClose();
    showLoader('Processing...');
    try {
      const response = await api.post('/subscription/add-user-slots/', {
        slot_size: parseInt(slots, 10)
      });
      if (response.status === 200 || response.status === 201) {
        setTimeout(() => {
          hideLoader();
          openSuccessModal('Request Submitted Successfully', 'User slot purchase request submitted successfully');
        }, 300);
      }
    } catch (error) {
      setTimeout(() => {
        hideLoader();
        openErrorModal('Failed to submit user slot purchase request', 'Failed to submit user slot purchase request');
      }, 300);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    if (isNaN(value)) return '₹0.00';
    return `₹${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4 }}>
        <Box sx={{
          background: 'rgba(25, 118, 210, 0.08)',
          borderRadius: '50%',
          width: 72,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}>
          <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          Add User Slots
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Add more users to your team to increase productivity and sales.
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{
          background: '#f5f7fa',
          borderRadius: 2,
          p: 2,
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center'
        }}>
          <Chip
            label={currentPlan?.plan_name}
            color="primary"
            size="medium"
            sx={{ fontWeight: 600, fontSize: 16 }}
          />
          <Typography variant="body2" color="text.secondary">
            Current Slots: <b>{currentPlan?.user_slots_used || 0}/{currentPlan?.total_user_slots || 0}</b>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cost per User: {formatCurrency(currentPlan?.per_user_cost || 0)}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
          Each user slot allows one additional team member.
        </Typography>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Number of Additional User Slots"
            type="text"
            value={slots}
            onChange={handleSlotsChange}
            onFocus={handleSlotsFocus}
            onBlur={handleSlotsBlur}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 0 }}
          />
        </Box>
        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Base Cost:</Typography>
            <Typography>{formatCurrency(cost.toFixed(2))}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>GST (18%):</Typography>
            <Typography>{formatCurrency(tax)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight="bold">Total Cost:</Typography>
            <Typography fontWeight="bold" color="primary">
              {formatCurrency(totalCost)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0, justifyContent: 'center', gap: 2 }}>
        <Button
          color="error"
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ minWidth: 120, fontWeight: 600 }}
        >
          Close
        </Button>
        <Button 
          color="primary"
          variant="contained" 
          onClick={handleSubmit}
          disabled={parseInt(slots, 10) <= 0 || isSubmitting}
          sx={{ minWidth: 180, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
          startIcon={<LockIcon />}
        >
          Purchase Slots
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserSlotModal;